import { useEffect, useRef, useState } from 'react';
import { FilterKindEnum } from '../../../../domains/global-filters/model/FilterChain';
import { getFilterMetadata } from '../../FilterChainUtils';
import { GenericFilter } from '../GenericBloc/GenericFilter/GenericFilter';
import type { AbstractSingleFilterPillsProps } from '../SingleFilterType';
import type { UserAttributeOptionType, UserAttributeType } from './UserAttributeFilterType';

import { UserAttributeSelector } from './UserAttributeSelector';
import { UserAttributeWrapper } from './UserAttributeWrapper';

interface UserAttributeFilterProps extends AbstractSingleFilterPillsProps<UserAttributeType> {
  userAttributeOptionList: UserAttributeOptionType[];
  userAttributeOptionIsLoading: boolean;
  onAddMoreUserAttribute?: () => void;
}

export function UserAttributeFilter({
  currentValue,
  autoFocus,
  onApply,
  onRemove,
  onDropdownHidden,
  showDropdownOnMount,
  userAttributeOptionList,
  userAttributeOptionIsLoading,
  onDropdownShown,
  onAddMoreUserAttribute,
}: UserAttributeFilterProps): JSX.Element {
  const [dirtyValue, setDirtyValue] = useState(currentValue);
  useEffect(() => {
    if (currentValue?.value) {
      setDirtyValue(currentValue);
    }
  }, [currentValue]);
  // We have to use a ref for the showSelector so that it updates before onDropdownHidden from GenericFilter is ran.
  const showSelectorRef = useRef<boolean>(!dirtyValue?.kind);
  const metadata = getFilterMetadata(FilterKindEnum.UserAttribute);

  const handleUserAttributeSelected = (newValue?: UserAttributeType) => {
    showSelectorRef.current = false;
    setDirtyValue(newValue);
  };

  if (showSelectorRef.current) {
    return (
      <GenericFilter
        autoFocus={autoFocus}
        filterName={metadata.filterName}
        label1={metadata.filterName}
        iconName={metadata.iconName}
        onRemove={onRemove}
        showDropdownOnMount={showDropdownOnMount}
        onDropdownHidden={() => {
          if (showSelectorRef.current) {
            onDropdownHidden?.();
          }
        }}
        onDropdownShown={onDropdownShown}
        renderDropdown={() => {
          return (
            <UserAttributeSelector
              filterName={metadata.filterName}
              userAttributeOptionList={userAttributeOptionList}
              userAttributeOptionIsLoading={userAttributeOptionIsLoading}
              onUserAttributeSelected={handleUserAttributeSelected}
              onAddMoreUserAttribute={onAddMoreUserAttribute}
            />
          );
        }}
      />
    );
  }
  return (
    <UserAttributeWrapper
      currentValue={dirtyValue}
      onApply={onApply}
      onRemove={onRemove}
      onDropdownHidden={onDropdownHidden}
      showDropdownOnMount={showDropdownOnMount ?? !currentValue}
      limitOperationsForUserId
    />
  );
}
